import { Users, FolderOpen, FileText, HardDrive, Activity, AlertCircle } from 'lucide-react'
import { useAuthedFetch } from '../../hooks/useAuthedFetch'
import { useTimedOut } from '../../hooks/useTimedOut'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import { humaniseAction } from './adminFormat'

interface AdminStats {
  user_count: number
  project_count: number
  document_count: number
  storage_bytes: number
  storage_mb: number
}

interface AuditEvent {
  event_id: string
  action_type: string
  timestamp: string
}

export default function AdminDashboard() {
  // Direct fetch, NOT the axios `api` instance: admin GETs made through axios
  // inside the /admin route context never settle their loading flag (see
  // hooks/useAuthedFetch.ts). The API returns { stats: {...} }.
  const { data: statsResp, isLoading: statsLoading, isError: statsError, refetch } =
    useAuthedFetch<{ stats: AdminStats }>('/admin/stats')
  const stats = statsResp?.stats

  const { data: activity, isError: activityError, refetch: refetchActivity } =
    useAuthedFetch<{ events: AuditEvent[]; count: number }>('/audit?limit=5')

  // Bound the skeleton so a slow/racy load never walls the whole page forever
  // (the exact Fig32 defect). Once timed out we render the page structure and
  // let the tiles fill in when the fetch settles.
  const loadTimedOut = useTimedOut(statsLoading)
  if (statsLoading && !loadTimedOut) return <LoadingSkeleton rows={4} />

  // Lambda returns { user_count, project_count, document_count, storage_bytes, storage_mb }
  // UX-046: a figure that did not load is "—", never 0. An admin reading "Total
  // Users 0" off a failed request is acting on a false all-clear.
  const dash = '\u2014'
  const statCards = [
    { label: 'Total Users', value: stats ? stats.user_count ?? dash : dash, icon: <Users size={20} />, color: 'bg-iflow-500/20 text-iflow-600' },
    { label: 'Projects', value: stats ? stats.project_count ?? dash : dash, icon: <FolderOpen size={20} />, color: 'bg-green-500/20 text-green-600' },
    { label: 'Documents', value: stats ? stats.document_count ?? dash : dash, icon: <FileText size={20} />, color: 'bg-purple-500/20 text-purple-600' },
    { label: 'Storage (MB)', value: stats?.storage_mb != null ? stats.storage_mb.toFixed(1) : dash, icon: <HardDrive size={20} />, color: 'bg-yellow-500/20 text-yellow-600' },
  ]

  const showLoadError = statsError || (loadTimedOut && !stats)

  return (
    <div>
      {/*
        P7 honesty: no status chip on this header. There is no platform-health
        endpoint in services/api.ts, so a green "Operational" would be fabricated
        (BUG-151). The header states what the screen is; the tiles below carry the
        only real, live numbers.
      */}
      <PageHeader
        title="Admin Dashboard"
        description="Platform-wide usage, users and audit — visible only to InnovateFlow administrators."
        helpKey="admin-overview"
      />

      {showLoadError && (
        <div role="alert" className="mb-4 flex items-center justify-between gap-3 p-3 bg-red-500/10 border border-red-200 rounded-lg text-sm text-red-700">
          <span className="flex items-center gap-2">
            <AlertCircle size={16} className="flex-shrink-0" />
            Couldn't load live platform totals.
          </span>
          <button onClick={refetch} className="font-medium text-red-700 hover:text-red-800 underline">
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat) => (
          <div key={stat.label} className="light-card p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{stat.label}</span>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                {stat.icon}
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Recent Activity — Lambda returns { events: [...], count } */}
        <div className="light-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={18} className="text-iflow-500" />
            <h2 className="text-lg font-semibold text-gray-800">Recent Activity</h2>
          </div>
          {activityError ? (
            <div role="alert" className="flex items-center justify-between gap-3 p-3 bg-red-500/10 border border-red-200 rounded-lg text-sm text-red-700">
              <span className="flex items-center gap-2">
                <AlertCircle size={16} className="flex-shrink-0" />
                Couldn't load recent activity.
              </span>
              <button onClick={refetchActivity} className="font-medium text-red-700 hover:text-red-800 underline">
                Retry
              </button>
            </div>
          ) : activity?.events?.length ? (
            <div className="space-y-3">
              {activity.events.slice(0, 5).map((entry) => (
                <div key={entry.event_id} className="flex items-start gap-3 py-2 border-b border-sky-200/50 last:border-0">
                  <div className="w-2 h-2 rounded-full bg-iflow-400 mt-1.5" />
                  <div>
                    <p className="text-sm text-gray-700">{humaniseAction(entry.action_type)}</p>
                    <p className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              compact
              icon={Activity}
              title="No recent activity"
              description="Security-relevant actions across the platform will appear here as they happen."
            />
          )}
        </div>
      </div>
    </div>
  )
}
